'use client';

import { useState } from 'react';
import { ZOOM_STEPS, type ViewerSource } from './types';
import { ViewerFinder, type ViewerFinderProps } from './viewer-finder';
import { useMailBrand } from '@/lib/hooks/use-mail-brand';
import { useWorkspaceConnected } from '@/lib/hooks/use-workspace-connected';

interface ViewerToolbarProps {
  source: ViewerSource;
  page: number;
  pageCount: number;
  onPrev: () => void;
  onNext: () => void;
  onJump: (page: number) => void;
  onClose?: () => void;
  /** Current dock style, and the toggle. Mirrors the AI box's control. */
  dockStyle?: 'overlay' | 'embedded';
  onToggleDockStyle?: () => void;
  /** Find-in-document. Optional so the toolbar renders without it in tests. */
  finder?: ViewerFinderProps;
  /** Zoom as a multiple of fit-to-width. The cluster renders only when given. */
  zoom?: number;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onZoomFit?: () => void;
}

export function ViewerToolbar({
  source, page, pageCount, onPrev, onNext, onJump, onClose,
  dockStyle = 'overlay', onToggleDockStyle, finder,
  zoom, onZoomIn, onZoomOut, onZoomFit,
}: ViewerToolbarProps) {
  const { docsApp } = useMailBrand();
  // No workspace connected → there is nowhere to create the editable copy,
  // so the Word / Docs button could only fail. Treat the document as having
  // no editor target and fall through to "Open in new tab" (the PDF).
  const workspaceConnected = useWorkspaceConnected();
  const hasEditorTarget =
    workspaceConnected &&
    Boolean(source.wordWebUrl || source.onOpenInWord || source.openInWordDisabledReason);
  // True while an `onOpenInWord` call is in flight. Local, not on `source`:
  // an open panel keeps the source it was handed, so a flag the caller sets
  // later would never reach it. The first open seeds the working copy and
  // takes seconds; without this the button looked dead and a second click
  // fired a second open.
  const [openingEditor, setOpeningEditor] = useState(false);
  // See the page input below — true only while the caseworker has emptied it.
  const [cleared, setCleared] = useState(false);

  return (
    <header className="flex flex-col gap-2 border-b border-line bg-surface px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
          {source.title}
        </h2>
        {/* Float / embed toggle — same control, icons and wording as the AI
            box's (FloatingAIBox), because it does the same thing to the same
            page and a caseworker meets both on one screen. */}
        {onToggleDockStyle && (
          <button
            type="button"
            onClick={onToggleDockStyle}
            aria-label={
              dockStyle === 'embedded'
                ? 'Switch document viewer to floating overlay'
                : 'Embed document viewer alongside the page'
            }
            title={dockStyle === 'embedded' ? 'Float over page' : 'Embed alongside page'}
            className="rounded-md p-1 text-stone-400 transition-colors hover:bg-line-firm hover:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/13"
          >
            {dockStyle === 'embedded' ? (
              // Currently embedded → this offers overlay: one panel over another.
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <rect x="3.5" y="3.5" width="13" height="13" rx="1.5" />
                <rect x="7.5" y="7.5" width="13" height="13" rx="1.5" fill="white" />
              </svg>
            ) : (
              // Currently floating → this offers embed: two columns side by side.
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <rect x="3" y="4" width="18" height="16" rx="1.5" />
                <line x1="14" y1="4" x2="14" y2="20" />
              </svg>
            )}
          </button>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close document"
            className="rounded p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-muted"
          >
            ×
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button
          type="button" onClick={onPrev} disabled={page <= 1}
          className="rounded border border-stone-300 px-2 py-1 text-content hover:bg-stone-50 disabled:opacity-40"
        >
          ‹ Prev
        </button>
        <span className="flex items-center gap-1 text-muted">
          Page
          <input
            type="number" min={1} max={pageCount || 1}
            // `page` is the source of truth EXCEPT while the box is
            // deliberately empty. Fully controlled on `page`, clearing the box
            // fired onChange with '' -> Number('') === 0 -> clamped to 1 ->
            // "1" written straight back into the field, so you could never
            // clear it to type a two-digit page. Holding the empty state
            // locally allows that without loosening the clamp: any non-empty
            // value still goes through `onJump` and the field still shows the
            // clamped result.
            value={cleared ? '' : page}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                setCleared(true);
                return;
              }
              setCleared(false);
              onJump(Number(raw));
            }}
            onBlur={() => setCleared(false)}
            className="w-12 rounded border border-stone-300 px-1 py-0.5 text-center"
            aria-label="Jump to page"
          />
          of {pageCount || '—'}
        </span>
        <button
          type="button" onClick={onNext} disabled={pageCount > 0 && page >= pageCount}
          className="rounded border border-stone-300 px-2 py-1 text-content hover:bg-stone-50 disabled:opacity-40"
        >
          Next ›
        </button>

        {zoom !== undefined && (
          <span className="flex items-center gap-1 text-muted">
            <button
              type="button" aria-label="Zoom out"
              aria-disabled={zoom <= ZOOM_STEPS[0]}
              onClick={zoom <= ZOOM_STEPS[0] ? undefined : onZoomOut}
              className="rounded border border-stone-300 px-2 py-1 text-content hover:bg-stone-50 aria-disabled:opacity-40"
            >
              −
            </button>
            <span aria-live="polite" className="min-w-[5ch] text-center tabular-nums">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button" aria-label="Zoom in"
              // aria-disabled, not disabled: a disabled button drops focus to
              // <body>, which would stop Escape / Ctrl+F reaching the panel.
              aria-disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
              onClick={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1] ? undefined : onZoomIn}
              className="rounded border border-stone-300 px-2 py-1 text-content hover:bg-stone-50 aria-disabled:opacity-40"
            >
              +
            </button>
            <button
              type="button" aria-label="Fit to width"
              aria-disabled={zoom === 1}
              onClick={zoom === 1 ? undefined : onZoomFit}
              className="rounded border border-stone-300 px-2 py-1 text-content hover:bg-stone-50 aria-disabled:opacity-40"
            >
              Fit
            </button>
          </span>
        )}

        <span className="ml-auto flex items-center gap-2">
          {/* Original format, never the rendered PDF — see Global Constraints. */}
          <a
            href={source.downloadUrl} download
            className="rounded border border-stone-300 px-2 py-1 text-content hover:bg-stone-50"
          >
            Download
          </a>
          {source.originalKind === 'DOCX' && hasEditorTarget && (
            <button
              type="button"
              disabled={openingEditor || !!source.openInWordDisabledReason}
              aria-busy={openingEditor}
              title={source.openInWordDisabledReason}
              onClick={() => {
                if (source.openInWordDisabledReason) return;
                // Prefer the callback: a caller that supplies one is minting
                // the Word target on demand (an edit session), and a
                // `wordWebUrl` alongside it would be the staler of the two.
                if (source.onOpenInWord) {
                  // Called synchronously, inside the click: the caller may
                  // need the click to claim a tab (lib/open-url-after.ts).
                  const result = source.onOpenInWord();
                  if (result instanceof Promise) {
                    setOpeningEditor(true);
                    void result.finally(() => setOpeningEditor(false));
                  }
                  return;
                }
                window.open(source.wordWebUrl as string, '_blank', 'noopener');
              }}
              className="rounded bg-accent px-2 py-1 font-medium text-white hover:bg-accent-ink disabled:cursor-wait disabled:opacity-60"
            >
              {openingEditor ? 'Opening…' : `Open in ${docsApp}`}
            </button>
          )}
          {/*
            A DOCX with no OneDrive working copy (e.g. a firm template preview
            rendered straight from stored DOCX bytes) has no "Open in Word"
            target. Fall back to the same "Open in new tab" control the PDF
            branch uses, pointed at the rendered PDF, so the action cluster is
            never left with only Download. The same applies when the firm has
            no workspace connected: no copy can be made, so the PDF it is.
          */}
          {(source.originalKind === 'PDF' ||
            (source.originalKind === 'DOCX' && !hasEditorTarget)) && (
            <button
              type="button"
              onClick={() => window.open(source.pdfUrl, '_blank', 'noopener')}
              className="rounded bg-accent px-2 py-1 font-medium text-white hover:bg-accent-ink"
            >
              Open in new tab
            </button>
          )}
        </span>
      </div>

      {/* Its own full-width row. In the page-nav row above it was a 160px box
          that the first reviewer to use the panel could not find at all. */}
      {source.actions && source.actions.length > 0 && (
        <div role="group" aria-label="Document actions" className="flex flex-wrap items-center gap-2 text-xs">
          {source.actions.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => void a.onClick()}
              disabled={!!a.disabledReason || !!a.busy}
              title={a.disabledReason}
              className={
                a.intent === 'primary'
                  ? 'rounded bg-accent px-2 py-1 font-medium text-white hover:bg-accent-ink disabled:opacity-40'
                  : 'rounded border border-stone-300 px-2 py-1 text-content hover:bg-stone-50 disabled:opacity-40'
              }
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
      {finder && <ViewerFinder {...finder} />}
    </header>
  );
}
